import { useState, useEffect, useCallback } from 'react';
import { getAllLookups } from '../models/externalLookupModel';
import api from '../services/api';

/**
 * Custom hook for managing MMS lookup data
 * Fetches and caches all lookup tables used for filtering and data enrichment
 * 
 * Usage:
 * const { lookups, loading, error, refresh } = useMmsLookups();
 * 
 * Returns:
 * - lookups: Object containing all lookup tables
 * - loading: Boolean indicating if data is being fetched
 * - error: Error message if fetch fails
 * - refresh: Function to manually refresh lookup data
 */
export const useMmsLookups = () => {
  const [lookups, setLookups] = useState({
    om_statuses: [],
    jc_statuses: [],
    priorities: [],
    application_types: [],
    jc_outcomes: [],
    jc_notes: [],
    stations: [],
    outcome_types: [],
    source_types: [],
    employees: [],
    maintenance_activities: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchLookups = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getAllLookups(); 
      
      // Handle response even if top-level success is false (partial failures)
      if (response.data) {
        // Extract data from each lookup table
        const extractedLookups = {};
        Object.keys(response.data).forEach(key => {
          const lookupData = response.data[key];
          extractedLookups[key] = lookupData.success && lookupData.data ? lookupData.data : [];
        });
        
        // Fetch workers with assignments from EDAMS
        try {
          const employeesResponse = await api.get('/api/external/employees', {
            params: { status: 1, limit: 1000 }
          });
          if (employeesResponse?.data?.success && employeesResponse?.data?.data) {
            // Store all active employees for the filter
            extractedLookups.employees = employeesResponse.data.data; 
          } else if (employeesResponse?.data?.data) {
            // Handle case where success flag is missing but data exists
            extractedLookups.employees = employeesResponse.data.data;
          } else {
            extractedLookups.employees = [];
          }
        } catch (empErr) {
          console.warn('Failed to load employees:', empErr);
          extractedLookups.employees = [];
        }
        
        // Fetch maintenance activities
        try {
          const activitiesResponse = await api.get('/api/external/lookup/maintenance-activities');
          if (activitiesResponse?.data?.success && activitiesResponse?.data?.data) {
            extractedLookups.maintenance_activities = activitiesResponse.data.data; 
          } else if (activitiesResponse?.data?.data) {
            // Handle case where success flag is missing but data exists
            extractedLookups.maintenance_activities = activitiesResponse.data.data;
          } else {
            extractedLookups.maintenance_activities = [];
          }
        } catch (actErr) {
          console.warn('Failed to load maintenance activities:', actErr);
          extractedLookups.maintenance_activities = [];
        }
         
        setLookups(extractedLookups);
        
        // Set error message if some lookups failed but continue with partial data
        if (!response.success) {
          console.warn('Some lookups failed:', response.message);
        }
      } else {
        throw new Error('No lookup data returned');
      }
    } catch (err) {
      console.error('Error fetching lookups:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLookups();
  }, [fetchLookups]);

  return {
    lookups,
    loading,
    error,
    refresh: fetchLookups
  };
};

/**
 * Custom hook for managing job cards with advanced filters
 * 
 * Usage:
 * const { 
 *   jobCards, 
 *   loading, 
 *   error, 
 *   filters, 
 *   setFilters, 
 *   refresh 
 * } = useJobCardsWithFilters();
 */
export const useJobCardsWithFilters = (initialFilters = {}) => {
  const [jobCards, setJobCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    start_date: initialFilters.start_date || (() => {
      const date = new Date();
      date.setDate(date.getDate() - 30);
      return date.toISOString().split('T')[0];
    })(),
    end_date: initialFilters.end_date || new Date().toISOString().split('T')[0],
    status: initialFilters.status || '',
    assigned_to: initialFilters.assigned_to || '',
    source_type: initialFilters.source_type || '',
    application_types: initialFilters.application_types || [],
    mactivity_no: initialFilters.mactivity_no || ''
  });

  const fetchJobCards = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Clear existing job cards immediately when fetching new data
      setJobCards([]);
      
      const { getJobCards } = await import('../models/externalModel');
      
      // Build query params
      const params = {
        start_date: filters.start_date,
        end_date: filters.end_date
      };
      
      if (filters.status) params.status = filters.status;
      if (filters.assigned_to) params.assigned_to = filters.assigned_to;
      if (filters.source_type) params.source_type = filters.source_type;
      if (filters.mactivity_no) params.mactivity_no = filters.mactivity_no;
      if (filters.application_types && filters.application_types.length > 0) {
        params.application_types = filters.application_types.join(',');
      }
      
      const response = await getJobCards(params);
      const jobs = Array.isArray(response) ? response : [];
      setJobCards(jobs);
      
      // Log first EDAMS job for debugging
      if (jobs.length > 0) {
        console.log('=== FIRST EDAMS JOB ===', jobs[0]);
      }
    } catch (err) {
      console.error('Error fetching job cards:', err);
      setError(err.message);
      setJobCards([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchJobCards();
  }, [fetchJobCards]);

  return {
    jobCards,
    loading,
    error,
    filters,
    setFilters,
    refresh: fetchJobCards
  };
};

/**
 * Helper hook to get human-readable status names
 */
export const useStatusMapper = () => {
  const { lookups } = useMmsLookups();

  const getOmStatusName = useCallback((statusCode) => {
    if (!lookups.om_statuses || lookups.om_statuses.length === 0) {
      return `Status ${statusCode}`;
    }
    
    const status = lookups.om_statuses.find(s => s.OM_STATUS === parseInt(statusCode));
    return status ? status.status_name : `Status ${statusCode}`;
  }, [lookups.om_statuses]);

  const getPriorityName = useCallback((priorityRank) => {
    if (!lookups.priorities || lookups.priorities.length === 0) {
      return 'Unknown';
    }
    
    const priority = lookups.priorities.find(p => p.PRIORITY_RANK === parseInt(priorityRank));
    return priority ? priority.PRIORITY : 'Unknown';
  }, [lookups.priorities]);

  const getApplicationTypeName = useCallback((typeId) => {
    if (!lookups.application_types || lookups.application_types.length === 0) {
      return 'Unknown Type';
    }
    
    const type = lookups.application_types.find(t => t.APPLICATION_TYPE_ID === parseInt(typeId));
    return type ? type.TYPE_NAME : 'Unknown Type';
  }, [lookups.application_types]);

  const getStationName = useCallback((stationNo) => {
    if (!lookups.stations || lookups.stations.length === 0) {
      return 'Unknown Station';
    }
    
    const station = lookups.stations.find(s => s.STATION_NO === parseInt(stationNo));
    return station ? station.NAME : 'Unknown Station';
  }, [lookups.stations]);

  return {
    getOmStatusName,
    getPriorityName,
    getApplicationTypeName,
    getStationName,
    lookups
  };
};

export default {
  useMmsLookups,
  useJobCardsWithFilters,
  useStatusMapper
};
